import React, {useState} from 'react';
import {View, Text, FlatList, ScrollView, TouchableOpacity} from 'react-native';
import {responsiveWidth} from 'react-native-responsive-dimensions';

import {
  useAppDispatch,
  useAppSelector,
  useAppNavigation,
  useViewport,
} from '../../hooks';
import {components} from '../../components';
import {theme} from '../../constants';
import {setScreen} from '../../store/slices/tabSlice';
import BottomTabBar from '../../navigation/BottomTabBar';
import {emitLocationPickerEvent} from '../../utils/locationEvents';
import {
  useGetProductsQuery,
  useGetCarouselQuery,
  useGetCategoriesQuery,
  useGetReviewsQuery,
} from '../../store/slices/apiSlice';

const Home: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigation = useAppNavigation();
  const viewport = useViewport();
  const locationState = useAppSelector((state) => state.locationSlice);

  const {
    data: carouselData,
    error: carouselError,
    isLoading: carouselLoading,
  } = useGetCarouselQuery();

  const {
    data: categoriesData,
    error: categoriesError,
    isLoading: categoriesLoading,
  } = useGetCategoriesQuery();

  const {
    data: productsData,
    error: productsError,
    isLoading: productsLoading,
  } = useGetProductsQuery();
  const {
    data: reviewsData,
    error: reviewsError,
    isLoading: reviewsLoading,
  } = useGetReviewsQuery();

  const dishes = Array.isArray(productsData) ? productsData : [];
  const carousel = Array.isArray(carouselData) ? carouselData : [];
  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const reviews = Array.isArray(reviewsData) ? reviewsData : [];
  const recommended = dishes?.filter((e) => e.isRecommended) ?? [];

  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);

  const handleLocationRequest = () => {
    emitLocationPickerEvent();
  };

  const updateCurrentSlideIndex = (e: any): void => {
    const contentOffsetX = e.nativeEvent.contentOffset.x;
    const currentIndex = Math.round(contentOffsetX / theme.sizes.width);
    setCurrentSlideIndex(currentIndex);
  };

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const renderHeader = () => {
    return (
      <components.Header
        basket={true}
        user={true}
        userImage={true}
        userName={true}
      />
    );
  };

  const renderLocationRequired = () => {
    return (
      <View
        style={{
          paddingHorizontal: 20,
          paddingVertical: 40,
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text
          style={{
            ...theme.fonts.DMSans_700Bold,
            fontSize: 18,
            color: theme.colors.textPrimary,
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          Veuillez entrez une adresse
        </Text>
        <Text
          style={{
            ...theme.fonts.DMSans_400Regular,
            fontSize: 14,
            color: theme.colors.textMuted,
            textAlign: 'center',
            marginBottom: 20,
          }}
        >
          Sélectionnez une adresse dans le périmètre autorisé en appuyant sur le texte en
          haut de l'écran.
        </Text>
        <components.Button
          title='Choisir une adresse'
          onPress={handleLocationRequest}
          containerStyle={{alignSelf: 'stretch'}}
        />
      </View>
    );
  };

  const renderCarousel = () => {
    const carouselWithImages = carousel.filter((item) => {
      const hasImage = item.bannerUrl || item.imageUrl;

      if (!hasImage) {
        return false;
      }

      if (!item.viewport) {
        return true;
      }

      return item.viewport === viewport;
    });

    const renderCarouselImages = () => {
      return (
        <FlatList
          data={carouselWithImages}
          onMomentumScrollEnd={(e) => updateCurrentSlideIndex(e)}
          renderItem={({item}) => (
            <components.Image
              source={{
                uri: (item.bannerUrl ?? item.imageUrl) as string,
              }}
              style={{width: theme.sizes.width, height: 250, aspectRatio: 1.5}}
            />
          )}
          pagingEnabled={true}
          keyExtractor={(item) => item.id}
          horizontal={true}
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          decelerationRate={0}
          bounces={false}
          alwaysBounceHorizontal={false}
        />
      );
    };

    const renderIndicator = () => {
      if (carouselWithImages.length > 1) {
        return (
          <View
            style={{
              height: 24,
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              bottom: 20,
              flexDirection: 'row',
              alignSelf: 'center',
            }}
          >
            {carouselWithImages.map((image, index) => {
              return (
                <View
                  key={index}
                  style={{
                    width: 8,
                    height: currentSlideIndex === index ? 20 : 8,
                    borderRadius: 8 / 2,
                    backgroundColor: theme.colors.backgroundPrimary,
                    opacity: currentSlideIndex === index ? 1 : 0.5,
                    borderColor:
                      currentSlideIndex === index
                        ? theme.colors.textPrimary
                        : '#DBE9F5',
                    marginHorizontal: 4,
                  }}
                />
              );
            })}
          </View>
        );
      }
      return null;
    };

    if (carouselWithImages.length > 0) {
      return (
        <View style={{marginBottom: 30}}>
          {renderCarouselImages()}
          {renderIndicator()}
        </View>
      );
    }

    if (carouselWithImages.length === 0) {
      return null;
    }
  };

  const renderCategories = () => {
    if (categories.length > 0) {
      return (
        <View style={{marginBottom: 30}}>
          <components.BlockHeading
            title='We offer'
            onPress={() => {
              dispatch(setScreen('Menu'));
            }}
            containerStyle={{marginHorizontal: 20, marginBottom: 14}}
          />
          <FlatList
            data={categories}
            horizontal={true}
            contentContainerStyle={{paddingLeft: 20}}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item) => item.id}
            pagingEnabled={true}
            decelerationRate={0}
            renderItem={({item}) => {
              const lastItem = categories[categories.length - 1];
              const isLast = item.id === lastItem?.id;

              return (
                <TouchableOpacity
                  onPress={() => {
                    navigation.navigate('Menulist', {
                      category: item.id,
                    });
                  }}
                  style={{marginRight: isLast ? 20 : 10}}
                >
                  <View style={{alignItems: 'center'}}>
                    {item.imageUrl ? (
                      <components.ImageBackground
                        source={{uri: item.imageUrl}}
                        style={{
                          width: 90,
                          height: 90,
                        }}
                        resizeMode='cover'
                        imageStyle={{borderRadius: 10}}
                      />
                    ) : (
                      <View
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: 10,
                          backgroundColor: theme.colors.backgroundPrimary,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      />
                    )}
                    <Text
                      style={{
                        ...theme.fonts.DMSans_400Regular,
                        fontSize: 10,
                        color: theme.colors.textPrimary,
                        textAlign: 'center',
                        marginTop: 8,
                      }}
                    >
                      {item.name}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            }}
          />
        </View>
      );
    }

    return null;
  };

  const renderRecommended = () => {
    const slice = recommended?.slice(0, 12);

    if (recommended.length > 0) {
      return (
        <View style={{marginBottom: 30}}>
          <components.BlockHeading
            title='Recommended for you'
            containerStyle={{marginHorizontal: 20, marginBottom: 14}}
          />
          <FlatList
            data={slice}
            horizontal={true}
            contentContainerStyle={{paddingLeft: 20}}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item) => item.id}
            pagingEnabled={true}
            snapToInterval={theme.sizes.width - responsiveWidth(44.2)}
            decelerationRate={0}
            renderItem={({item, index}) => {
              const lastItem = index === slice.length - 1;
              return (
                <components.RecommendedItem item={item} lastItem={lastItem} />
              );
            }}
          />
        </View>
      );
    }

    if (recommended.length === 0) {
      return null;
    }
  };

  const renderReviews = () => {
    const slice = reviews?.slice(0, 12);

    return (
      <View style={{marginBottom: 20}}>
        <components.BlockHeading
          title='Our Happy clients say'
          onPress={() => {
            navigation.navigate('Reviews');
          }}
          containerStyle={{marginHorizontal: 20, marginBottom: 14}}
        />
        <FlatList
          data={slice}
          horizontal={true}
          contentContainerStyle={{paddingLeft: 20}}
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item) => item.id}
          pagingEnabled={true}
          snapToInterval={theme.sizes.width - 54}
          decelerationRate={0}
          renderItem={({item, index}) => {
            const last = index === reviews.length - 1;
            return <components.ReviewItem item={item} last={last} />;
          }}
        />
      </View>
    );
  };

  const renderContent = () => {
    if (carouselLoading || categoriesLoading || productsLoading || reviewsLoading) {
      return <components.Loader />;
    }
    if (!locationState.isAddressConfirmed) {
      return (
        <ScrollView
          contentContainerStyle={{flexGrow: 1, paddingTop: 6, flex: 1}}
          showsVerticalScrollIndicator={false}
        >
          {renderLocationRequired()}
        </ScrollView>
      );
    }

    return (
      <ScrollView
        contentContainerStyle={{flexGrow: 1, paddingTop: 6}}
        showsVerticalScrollIndicator={false}
      >
        {renderCarousel()}
        {renderCategories()}
        {renderRecommended()}
        {renderReviews()}
      </ScrollView>
    );
  };

  const renderBottomTabBar = () => {
    return <BottomTabBar />;
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderContent()}
      {renderBottomTabBar()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default Home;
